import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useToast from 'contexts/ToastProvider/useToast';
import { calendarSyncLogger } from 'utils/logging/scopedLogger';
import { CustomMeedioMatrixEvents } from 'utils/matrixUtils';

import { CalendarType } from './CalendarRow';
import {
  getAccountCalendarsFromMatrix,
  getMainAccountDataFromMatrix,
  getUniqCalendarItems,
} from '../../CalendarSyncModal/calendarHelpers';

export type MainCalendarInfo = {
  provider: string;
  email: string;
  id: string;
};

const useUpdateCalendars = () => {
  const { matrixClient, isInitialSyncFinished } = useMatrixContext({ requireDefinedMatrixClient: false });
  const { pushToast } = useToast();
  const { t } = useTranslation();
  const [accountCalendars, setAccountCalendarsState] = useState<CalendarType[]>([]);
  const [mainCalendarInfo, setMainCalendarInfoState] = useState<MainCalendarInfo>({} as MainCalendarInfo);
  const [isCalendarAccountStateReady, setIsCalendarAccountStateReady] = useState(false);

  const updateCalendarItem = (checked: boolean, id: string) => {
    const doesCalendarItemExist = accountCalendars.some((cal) => cal.id === id);
    const updatedCalendars = doesCalendarItemExist
      ? accountCalendars.map((cal) => (cal.id === id ? { ...cal, isSynced: checked } : cal))
      : [...accountCalendars, { id, isSynced: checked } as CalendarType];

    return matrixClient
      ?.setAccountData(CustomMeedioMatrixEvents.CALENDAR, { calendars: updatedCalendars } as never)
      .then(() => setAccountCalendarsState(updatedCalendars))
      .catch((error) => {
        calendarSyncLogger.error('Error updating calendar item', { id, checked, error });
        throw error;
      });
  };

  const setMainCalendarInfo = (info: MainCalendarInfo) => {
    if (!matrixClient) throw new Error('Matrix client is not available');

    return matrixClient
      .setAccountData(CustomMeedioMatrixEvents.MAIN_CALENDAR_INFO, { info } as never)
      .then(() => setMainCalendarInfoState(info))
      .catch((error) => {
        calendarSyncLogger.error('Failed to set main calendar info:', error);
        return pushToast({ title: t('primary_calendar_error'), variant: 'error' });
      });
  };

  const resetMainCalendarInfo = () =>
    matrixClient?.setAccountData(CustomMeedioMatrixEvents.MAIN_CALENDAR_INFO, { info: {} } as never)?.catch((error) => {
      calendarSyncLogger.error('Error resetting main calendar info', { error });
      throw error;
    });

  useEffect(() => {
    if (!isInitialSyncFinished || isCalendarAccountStateReady) return;

    const rawAccountCalendars = getAccountCalendarsFromMatrix(matrixClient);
    const initialAccountCalendars = getUniqCalendarItems(rawAccountCalendars);
    const initialMainCalendarInfo = getMainAccountDataFromMatrix(matrixClient);

    setAccountCalendarsState(initialAccountCalendars);
    setMainCalendarInfoState(initialMainCalendarInfo);
    setIsCalendarAccountStateReady(true);
  }, [isCalendarAccountStateReady, isInitialSyncFinished, matrixClient]);

  return {
    updateCalendarItem,
    accountCalendars,
    mainCalendarInfo,
    setMainCalendarInfo,
    resetMainCalendarInfo,
    setAccountCalendarsState,
    setMainCalendarInfoState,
    isCalendarAccountStateReady,
  };
};

export default useUpdateCalendars;
